import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as model from '../../../models/CandidatesModel';
import { apiError } from '../../../helpers/ErrorHelper';
import { useAuth } from '../../../context/AuthContext';
import { useNotificationCount } from '../../../context/NotificationCountContext';
import { InputFile, MyReactSelect, MyReactSelectMultiple, Text, TextArea, TextEmail, TextFloat, TextNumber } from '../../inputs/inputs';
import { getData as getClients } from '../../../models/ClientsModel';
import { getData as getRequirements } from '../../../models/RequirementsModel';
import { getData as getSkills } from '../../../models/SkillsModel';
import * as candidateStatusModel from '../../../models/CandidateStatusModel';
import AdminLayout from '../../layout/AdminLayout';
import { Helmet } from 'react-helmet';
import PageHeader from '../../common/PageHeader';
import { formatDataBeforeDisplay, formatDataBeforeSend, quickAddClient, quickAddSkill, validorSettings } from '../../../helpers/GeneralHelper';
import SimpleReactValidator from 'simple-react-validator';
import { QuickAdd } from '../../modals/QuickAdd';
import { checkPermission } from "../../../helpers/GeneralHelper";
import { InterviewAddFromCandidate } from '../../modals/InterviewAddFromCandidate';
import WorkflowHelpBanner from '../../common/WorkflowHelpBanner';
import CandidateNextActionBanner from '../../common/CandidateNextActionBanner';
import CandidateTimeline from '../../common/CandidateTimeline';
import HelpTooltip from '../../common/HelpTooltip';
import { buildSelectOption } from '../../../helpers/PipelineFilterHelper';
import { normalizeResumePhone } from '../../../helpers/ResumeToCandidateHelper';
import {
  CANDIDATE_PIPELINE_RULES,
  validatePipelineForm,
} from '../../../helpers/PipelineFormValidationHelper';

export const CandidateForm = () => {

  const location = useLocation();
  const isEdit = location.pathname.slice(location.pathname.lastIndexOf("/") + 1, location.pathname.length) == 'edit';
  const isView = location.pathname.slice(location.pathname.lastIndexOf("/") + 1, location.pathname.length) == 'view';
  const module = location.state?.module;
  const data = location.state?.data;
  const prefill = location.state?.prefill;
  const baseURL = process.env.REACT_APP_BASE_URL;

  const inputFields = {
    candidate_firstname: '',
    candidate_lastname: '',
    candidate_client_id: '',
    candidate_requirement_id: '',
    candidate_mobile: '',
    candidate_email: '',
    candidate_skills: [],
    candidate_experience_total: '',
    candidate_experience_relevant: '',
    candidate_present_location: '',
    candidate_preffered_location: '',
    candidate_current_ctc: '',
    candidate_offered_ctc: '',
    candidate_education: '',
    candidate_current_company: '',
    candidate_notice_period: '',
    candidate_remarks: '',
    candidate_resume_id: '',
    candidate_resume_upload_id: '',
    candidate_status_id: '',
    candidate_comments: '', 
  }

  const formatOptions = [
    {
      type: 'react-select',
      column: 'candidate_client_id',
      labelColumn: 'client_name'
    },
    {
      type: 'react-select',
      column: 'candidate_requirement_id',
      labelColumn: 'requirement_name'
    },
    {
      type: 'react-select',
      column: 'candidate_status_id',
      labelColumn: 'status_name'
    },
    {
      type: 'react-select-multiple',
      column: 'candidate_skills',
    },
    {
      type: 'file',
      column: 'candidate_resume_id',
    },
  ];

  const { notificationCount, setNotificationCount } = useNotificationCount();

  const navigate = useNavigate(); 

  const { state } = useAuth();
  let user_data = state.user_data;
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [id, setId] = useState('');
  const [statusId, setStatusId] = useState('');
  const [fields, setFields] = useState(inputFields);

  const [quickModals, setQuickModals] = useState({ clients: false, requirements: false, skills: false });

  const [clients, setClients] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [candidateStatus, setCandidateStatus] = useState([]);
  const [skills, setSkills] = useState([]);
  const [toggleInterviewModal, setToggleInterviewModal] = useState(false);
  const [resumePrefillApplied, setResumePrefillApplied] = useState(false);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const result = await getClients({user_token: user_data.user_token, page: 1, limit: -1});
        if (! result) return;

        const clientOptions = result.clients.map(client => ({
          value: client._client_id,
          label: client.client_name
        }));

        setClients(clientOptions);
      } catch (error) {
        console.error(error);
      }
    };

    const fetchStatus = async() => {
      try {
        let apiParam = {user_token: user_data.user_token, page: 1, limit: -1};
        const response = await candidateStatusModel.getData(apiParam);

        if (typeof response != typeof undefined) {
          const options = response.candidate_status.map(ele => ({
            value: ele._status_id,
            label: ele.status_name,
            isScheduled: ele.status_schedule_interview
          }));
          //console.log(options);
          setCandidateStatus(options);
          if (!data) {
            const defaultStatus =
              options.find((opt) => opt.value == 20) ||
              options.find((opt) => String(opt.label).toLowerCase().includes("new")) ||
              options[0];
            if (defaultStatus) {
              setFields((prev) => ({
                ...prev,
                candidate_status_id: defaultStatus,
              }));
            }
          }
        }
      } catch (error) {
          console.error(error);
      }
    }

    const fetchSkills = async() => {
      try {
        let apiParam = {user_token: user_data.user_token, page: 1, limit: -1};
        const response = await getSkills(apiParam);

        if (typeof response != typeof undefined) {
          const options = response.skills.map(ele => ({
            value: ele._skill_id,
            label: ele.skill_name
          }));

          setSkills(options);
        }
      } catch (error) {
        console.error(error);
      }
    }

    fetchClients();
    fetchStatus();
    fetchSkills();
  }, []);

  const [loadingRequirements, setLoadingRequirements] = useState(false);
  useEffect(() => {
    const fetchRequirements = async() => {
      if (! fields.candidate_client_id) {
        fields.candidate_requirement_id = '';
        setRequirements([]);
        return;
      }

      setLoadingRequirements(true);

      try {
        let apiParam = {user_token: user_data.user_token, 'requirement_client_id': fields.candidate_client_id.value, page: 1, limit: -1};
        const response = await getRequirements(apiParam);

        if (typeof response != typeof undefined) {
          let options = [];
          let flag = false;
          response.requirements.forEach(function(item) {
            if (fields.candidate_requirement_id == item._requirement_id || fields.candidate_requirement_id?.value == item._requirement_id) {
              flag = true;
            }
            options.push({
              value: item._requirement_id,
              label: item.requirement_name
            });
          });

          if (! flag) {
            fields.candidate_requirement_id = '';
          }

          setRequirements(options);
        }
      } catch (error) {
        console.error(error);
      }

      setLoadingRequirements(false);
    }

    fetchRequirements();
  }, [fields.candidate_client_id]);

  useEffect(() => {
    if (data || !prefill?.clientId || !clients.length) return;
    const clientOpt =
      clients.find((c) => String(c.value) === String(prefill.clientId)) ||
      buildSelectOption(prefill.clientId, prefill.clientName);
    if (clientOpt) {
      setFields((prev) => ({ ...prev, candidate_client_id: clientOpt }));
    }
  }, [clients, prefill, data]);

  useEffect(() => {
    if (data || !prefill?.requirementId || !requirements.length) return;
    const reqOpt =
      requirements.find((r) => String(r.value) === String(prefill.requirementId)) ||
      buildSelectOption(prefill.requirementId, prefill.requirementName);
    if (reqOpt) {
      setFields((prev) => ({ ...prev, candidate_requirement_id: reqOpt }));
    }
  }, [requirements, prefill, data]);

  useEffect(() => {
    if (data || resumePrefillApplied || !prefill?.candidateFields) return;

    const needsSkills =
      Array.isArray(prefill.candidateFields.candidate_skills) &&
      prefill.candidateFields.candidate_skills.length > 0;
    if (needsSkills && !skills.length) return;

    setFields((prev) => ({
      ...prev,
      ...prefill.candidateFields,
      candidate_status_id: prev.candidate_status_id || prefill.candidateFields.candidate_status_id,
    }));
    setResumePrefillApplied(true);
  }, [data, prefill, skills, resumePrefillApplied]);

  useEffect(() => {
    if (data) {
      setId(data[module.primaryCol]);
      setFields(formatDataBeforeDisplay(data, formatOptions));
      setErrors({});
    }

    const fetchSelectedSkills = async() => {
      if (! data?._candidate_id) {
        return;
      }

      try {
        const response = await model.getSelectedSkills(user_data.user_token, data._candidate_id);

        if (typeof response != typeof undefined) {
          let skills = [];
          response.data.skills.forEach(function(item) {
            skills.push({ value: item._skill_id, label: item.skill_name });
          });

          setFields((prev) => ({ ...prev, 'candidate_skills': skills }));
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchSelectedSkills();
  }, [data]);
  const checkSchedule = (event) => {
    const selectedOption = candidateStatus.find(
      (option) => option.value === event
    ); // Find the matching object
    const isScheduled = selectedOption ? selectedOption.isScheduled : 0;
    if(isScheduled==1 && data){
      //console.log("isScheduled:", isScheduled);
      //if(data){
        setToggleInterviewModal(true);
        setStatusId(event);
      //}
    }
  };

  const openScheduleInterview = () => {
    if (!data) return;
    const currentStatusId =
      fields.candidate_status_id?.value ??
      fields.candidate_status_id ??
      data.candidate_status_id;
    setStatusId(currentStatusId);
    setToggleInterviewModal(true);
  };
  const validator = useRef(new SimpleReactValidator(validorSettings()));

  const getSubmitFields = () => ({
    ...fields,
    candidate_mobile: normalizeResumePhone(fields.candidate_mobile),
  });

  const submit = async(e) => {
    e.preventDefault();

    const submitFields = getSubmitFields();
    if (submitFields.candidate_mobile !== fields.candidate_mobile) {
      setFields((prev) => ({
        ...prev,
        candidate_mobile: submitFields.candidate_mobile,
      }));
    }

    if (!isView) {
      const pipelineValidation = validatePipelineForm(submitFields, CANDIDATE_PIPELINE_RULES);
      if (!pipelineValidation.valid) {
        setErrors((prev) => ({ ...prev, ...pipelineValidation.errors }));
        toast.error("Please fill all required fields.");
        return;
      }

      const normalizedMobile = normalizeResumePhone(submitFields.candidate_mobile);
      if (normalizedMobile.length !== 10) {
        setErrors((prev) => ({
          ...prev,
          candidate_mobile: "Enter a valid 10-digit mobile number.",
        }));
        toast.error("Please check the form errors!");
        return;
      }
      submitFields.candidate_mobile = normalizedMobile;
    }

    if (!isView) {
      validator.current.showMessages();
      if (!validator.current.allValid()) {
        toast.error('Please check the form errors!');
        return;
      }
    }
    
    setLoading(true);

    try {
      // Clear previous errors
      setErrors({});

      const formData = formatDataBeforeSend(submitFields, inputFields, formatOptions);

      let response;
      if (data) {
        response = await model.update(user_data.user_token, id, formData);
      } else {
        response = await model.create(user_data.user_token, formData);
      }

      if (response.status) {
        setNotificationCount(localStorage.getItem('unread_notification_count'));
        toast.success(response.message);
        model.saveToStorage(response.data[module.key], response.data.pagination);
        navigate('/' + module.route);
      } else {
        setErrors(response.error);
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);

      if (error.response?.data?.invalid_token == 1) {
        navigate('/re-login');
      }
    }

    setLoading(false);
  }

  if (! module) {
    navigate('/candidates');
    return;
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>{ isView ? 'View' : isEdit ? 'Edit' : 'Add'} {module.singularTitle} - Digital CMS</title>
      </Helmet>

      <div className="main-content app-content">
        <div className="container-fluid cms-form-shell">
          <PageHeader module={module} />

          {!isView && <WorkflowHelpBanner stage="candidates" />}

          {prefill?.fromResume && !data && (
            <div className="candidate-resume-prefill-banner mb-3">
              <i className="ri-file-user-line me-2" />
              Prefilled from resume
              {prefill.resumeName ? `: ${prefill.resumeName}` : ""}. Review all fields before saving.
              {prefill.resumeFileId ? (
                <span className="d-block mt-1 small">
                  Original resume file will be attached automatically
                  {prefill.resumeFileName ? `: ${prefill.resumeFileName}` : ""}.
                </span>
              ) : null}
            </div>
          )}

          {(data?.candidate_resume_upload_id) && (
            <div className="candidate-resume-source-banner mb-3">
              <i className="ri-link me-2" />
              Linked to parsed resume
              {data?.candidate_resume_upload_name
                ? `: ${data.candidate_resume_upload_name}`
                : `#${data.candidate_resume_upload_id}`}
              .
              <Link to="/resumes" className="ms-2">
                Open resumes library
              </Link>
            </div>
          )}

          {data && isView && (
            <CandidateNextActionBanner
              candidate={data}
              statusOptions={candidateStatus}
              module={module}
              onScheduleInterview={openScheduleInterview}
            />
          )}

          <form onSubmit={submit}>
            <div className="row">
              <div className="col-sm-9">
                <div className="card custom-card">
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="card-title">{ isView ? 'View' : isEdit ? 'Edit' : 'Add'} {module.singularTitle}</div>
                  </div>
                  <div className="card-body">
                    <div className="row gy-2">
                      <div className="col-xl-4 mb-3">
                        <Text column="candidate_firstname" label="First Name" required={!isView} fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <Text column="candidate_lastname" label="Last Name" required={!isView} fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>
                      
                      <div className="col-xl-4 mb-3">
                        <MyReactSelect column="candidate_client_id" label="Client" required={!isView} options={clients} fields={fields} setFields={setFields} errors={errors} quickAddKey="clients" quickAddSet={setQuickModals} disabled={isView} validator={!isView ? validator : null} rules={!isView ? "required" : ""} />
                        <HelpTooltip text="Pick the client company first — the requirement list will update automatically." />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <MyReactSelect column="candidate_requirement_id" label="Requirement" required={!isView} options={requirements} fields={fields} setFields={setFields} errors={errors} disabled={!isView ? loadingRequirements : isView} validator={!isView ? validator : null} rules={!isView ? "required" : ""} />
                        <HelpTooltip text="Each candidate must be linked to one open requirement (job order)." />
                        {/* quickAddKey="requirements" quickAddSet={setQuickModals} */}
                      </div>

                      <div className="col-xl-4 mb-3">
                        <TextNumber column="candidate_mobile" label="Mobile" required={!isView} fields={fields} setFields={setFields} errors={errors} validator={validator} rules='required' disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <TextEmail column="candidate_email" label="Email" required={!isView} fields={fields} setFields={setFields} errors={errors} validator={validator} rules='required|email' disabled={isView} />
                      </div>
                      
                      <div className="col-xl-4 mb-3">
                        <MyReactSelectMultiple column="candidate_skills" label="Skills" options={skills} fields={fields} setFields={setFields} errors={errors} quickAddKey="skills" quickAddSet={setQuickModals} disabled={isView} />
                      </div>
                      
                      <div className="col-xl-4 mb-3">
                        <TextNumber column="candidate_experience_total" label="Years of Experience (Total)" required={!isView} fields={fields} setFields={setFields} errors={errors} validator={validator} rules='required|max:2' disabled={isView} />
                      </div>
                      
                      <div className="col-xl-4 mb-3">
                        <TextNumber column="candidate_experience_relevant" label="Years of Experience (Relevant)" fields={fields} setFields={setFields} errors={errors} validator={validator} rules='max:2' disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <Text column="candidate_present_location" label="Present Location" required={!isView} fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <Text column="candidate_preffered_location" label="Preferred Location" required={!isView} fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <TextFloat column="candidate_current_ctc" label="Current CTC" fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <TextFloat column="candidate_offered_ctc" label="Offered CTC" fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <Text column="candidate_education" label="Education" fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <Text column="candidate_current_company" label="Current Company" fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <Text column="candidate_notice_period" label="Notice Period" required={!isView} fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                      </div>

                      <div className="col-xl-4 mb-3">
                        <InputFile column="candidate_resume_id" label="Resume"  fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                        <small>Formats: PDF & DOCX</small>
                      </div>
                    </div>
                  </div>
                </div>
              {(data) ?
                <div className="row gy-2 mt-1">
                  <div className="col-xl-12">
                    <div className="card custom-card">
                      <div className="card-body">
                        <h5 className="card-title mb-3 text-center">Resume Viewer</h5>
                        <iframe 
                          src={`https://docs.google.com/gview?url=${process.env.REACT_APP_ASSETS_URL}/${data.candidate_resume_id_path}&embedded=true`}
                          style={{ width: "100%", height: "100%", minHeight: "500px" }}
                        ></iframe>
                    
                        <h5 className="card-title mb-3 mt-5">Links</h5>
                        <div className="row gy-2 mb-3">
                          <div className="col-md-6">
                            {/* <a href={`/interviews/?_candidate_id=${id}`} className="btn w-100 btn-success">Interviews</a> */}
                            <Link to={`/interviews/?_candidate_id=${id}`} className="btn w-100 btn-success">Interviews</Link>
                          </div>
                          <div className="col-md-6">
                            {/* <a href={`/onboards/?_candidate_id=${id}`} className="btn w-100 btn-success">Onboards</a> */}
                            <Link to={`/onboards/?_candidate_id=${id}`} className="btn w-100 btn-success">Onboards</Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                : ''
              }
              </div>

              <div className="col-sm-3">
                <div className="card custom-card">
                  <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
                    { (isView && checkPermission(module.route, 'edit')) ? 
                    <a className="btn btn-icon btn-sm btn-info" title="Edit" onClick={() => navigate(`/candidates/${data.candidate_unique_id}/edit`,{ state: { data: data, module: module, }, })}>
                      <i className='bx bxs-edit'></i>
                    </a> :
                    <div>
                      <button className="btn btn-primary btn-md" disabled={loading}>
                        {loading ? <span><span className="spinner-border spinner-border-sm align-middle"></span> Loading...</span> : 'Save'}
                      </button>
                    </div>}

                    <div className="d-flex flex-wrap gap-2">
                      <Link className="btn btn-light btn-sm float-end" to="/candidates">
                        <i className="bi bi-reply me-1 fw-semibold"></i> Back
                      </Link>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="col-sm-12 mb-3">
                      <label className="form-label">
                        Status
                        <HelpTooltip text="After saving, set a status that schedules an interview to open the interview form automatically." />
                      </label>
                      <MyReactSelect
                        column="candidate_status_id"
                        label=""
                        options={candidateStatus}
                        onChange={(option) => checkSchedule(option?.value)}
                        fields={fields}
                        setFields={setFields}
                        errors={errors}
                        disabled={isView}
                        required={!isView}
                        validator={!isView ? validator : null}
                        rules={!isView ? "required" : ""}
                      />
                    </div>
                    <div className="col-sm-12 mb-3">
                      <TextArea column="candidate_remarks" label="Remarks" fields={fields} setFields={setFields} errors={errors} disabled={isView} />
                    </div>
                  </div>
                </div>
              {data?._candidate_id ? (
                <CandidateTimeline
                  candidateId={data._candidate_id}
                  userToken={user_data?.user_token}
                />
              ) : null}
              </div>
            </div>
          </form>
        </div>
      </div>
      <QuickAdd title="Client" column="candidate_client_id" setFieldOptions={setClients} modalKey="clients" showModal={quickModals.clients} form={quickAddClient} setShowModal={setQuickModals} setFields={setFields} />
      {/* <QuickAdd title="Requirement" column="candidate_requirement_id" dependedColumn="candidate_client_id" setFieldOptions={setRequirements} modalKey="requirements" showModal={quickModals.requirements} form={quickAddRequirement} setShowModal={setQuickModals} setFields={setFields} /> */}
      <QuickAdd title="Skill" column="candidate_skills" setFieldOptions={setSkills} isMultiple={true} modalKey="skills" showModal={quickModals.skills} form={quickAddSkill} setShowModal={setQuickModals} setFields={setFields} />
      {(data) ? 
        <InterviewAddFromCandidate showModal={toggleInterviewModal} setShowModal={setToggleInterviewModal} candidateData={data} statusId={statusId} />
        : ''
      }
      
      <Helmet></Helmet>
    </AdminLayout>
  );
};
